import { z } from 'zod';

/**
 * The HTTP client. One place, deliberately thin, and deliberately not generated.
 *
 * Two properties matter more than convenience here:
 *
 *   1. **Nothing is trusted.** Every response body is parsed by a Zod schema before it becomes a
 *      typed value. `eslint.config.js` treats the `no-unsafe-*` family as load-bearing for
 *      auditability rather than stylistic — a value that decays to `any` satisfies a PHI-shaped
 *      parameter without the compiler checking anything. A parsed boundary is what keeps that
 *      promise true at the one place untyped data actually enters.
 *   2. **Failures are distinguishable.** "Not signed in", "that endpoint does not exist yet",
 *      "the server sent something this build does not recognise" and "the network is down" are
 *      four different situations, and collapsing them into one generic error is how a missing
 *      endpoint gets diagnosed as a login bug. `ApiError.code` keeps them apart.
 */

/**
 * The error envelope `server/src/app.ts` returns: `{ error: 'unauthenticated' }` and friends.
 *
 * `denialReason` is optional because only one failure shape carries it —
 * `{ error: 'chart_access_denied', denialReason: ChartAccessDenialReason }`
 * (`server/src/clinical/patientsRoutes.ts` and every sibling clinical route) — and this envelope
 * is shared by every other error response, which has no such field.
 */
const ERROR_BODY = z.object({ error: z.string(), denialReason: z.string().optional() });

export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached a server. */
  readonly status: number;
  /** A machine-readable reason. The server's own `error` string where there was one. */
  readonly code: string;
  /**
   * Seconds until a `rate_limited` response's window resets, from the server's own `Retry-After`
   * header (`server/src/security/rateLimiter.ts`). `undefined` for every other code — never a
   * client-side guess, since only the server knows the window it actually applied.
   */
  readonly retryAfterSeconds: number | undefined;
  /**
   * The server's own reason a `chart_access_denied` response was denied — one of `@emr/shared`'s
   * `ChartAccessDenialReason` values, kept as a plain `string` here rather than importing that
   * union: this file is the one place untyped wire data crosses the trust boundary, and a reader
   * of `chartAccess.ts`'s copy table decides what to do with a value this build does not
   * recognise, not this class. `undefined` for every code other than `chart_access_denied`.
   */
  readonly denialReason: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    options?: {
      cause?: unknown;
      retryAfterSeconds?: number | undefined;
      denialReason?: string | undefined;
    },
  ) {
    super(message, { cause: options?.cause });
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = options?.retryAfterSeconds;
    this.denialReason = options?.denialReason;
  }
}

/** No session. The signal a route guard acts on; never shown as an error to the user. */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * A specific patient's chart was reached, but this viewer's role, treatment relationship, or the
 * record's own confidentiality label refused it — `shared/src/careTeam.ts`'s `decideChartAccess`.
 * Distinct from a missing capability (which hides the destination entirely, per
 * `shell/navigation.ts`) and from a generic failure: a clinician who can read charts in general can
 * still be refused this one, and the reason is worth showing rather than folding into
 * `FailureNotice`'s generic copy.
 */
export function isChartAccessDenied(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'chart_access_denied';
}

/**
 * The API answered, but not in a shape this build understands.
 *
 * Called out separately because it is the failure that will actually happen while the server side
 * of this app is still being written: a 404 from a route that does not exist yet, or a login
 * response whose fields moved. Both must be *visible*. Treating either as "not signed in" would
 * quietly bounce the user to the login page and hide the real problem.
 */
export function isContractMismatch(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === 'unexpected_response' || error.code === 'endpoint_missing')
  );
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

export interface RequestOptions {
  // `PUT` added for `api/orgBranding.ts` (`BACKLOG.md` B4) — the first browser-side caller of a
  // `PUT` route; several server route files already register `.put(...)` (`orgAiSettingsRoutes.ts`,
  // `orgs/brandingRoutes.ts`), this union just never needed the method before now.
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialised as JSON when present. Omit it entirely for a request with no body. */
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /**
   * Extra headers, merged on top of (never able to remove) the ones `apiRequest` always sends.
   * Added for `api/kiosk.ts`'s check-in surface (`BACKLOG.md` C7): that surface authenticates with
   * `Authorization: Bearer <token>` instead of the ordinary session cookie, and every other property
   * of this client — parsed responses, the same `ApiError` shape, the same `X-Requested-With` CSRF
   * header — still applies to it unchanged, so extending this client is the "favor the general,
   * reusable solution" choice over a second, parallel fetch wrapper.
   */
  readonly headers?: Record<string, string>;
}

function parseBody(text: string): unknown {
  if (text === '') return undefined;
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed;
  } catch {
    return undefined;
  }
}

/**
 * Build the `ApiError` for a non-2xx response — shared by `apiRequest` (a JSON-returning request)
 * and `requestBlob` (a binary-returning one), since a failure response's *envelope* is always the
 * same JSON error shape regardless of what a success response returns. Extracted once `requestBlob`
 * became a second call site needing this exact branch, per `claude-kit/rules/invariants.md`'s
 * "favor the general, reusable solution once a pattern actually recurs" — every case, comment, and
 * precedence choice below is unchanged from `apiRequest`'s own prior inline version.
 */
async function errorForResponse(
  response: Response,
  method: string,
  path: string,
): Promise<ApiError> {
  const raw = parseBody(await response.text());
  const parsed = ERROR_BODY.safeParse(raw);

  // `endpoint_missing` only when the body does NOT parse as a real error shape — checked first,
  // before ever reading `parsed.data.error`. The original version of this branch fired on
  // `response.status === 404` alone, ahead of the body parse, which meant a genuine
  // `{ error: 'not_found' }` from a real, built route (`GET /api/patients/:id` for a deleted or
  // mistyped id) could never reach the user as "that record does not exist" — every 404 read as
  // "the API did not answer as this app expects", regardless of what the server actually sent.
  if (!parsed.success) {
    if (response.status === 404) {
      // Distinguished on purpose. While the server half of this app is still being written, a
      // 404 with no real error body means "not built yet", and reporting it as a generic failure
      // sends the reader looking for a bug in the client.
      return new ApiError(
        404,
        'endpoint_missing',
        `The API has no ${method} ${path} endpoint. It may not be built yet.`,
      );
    }
    const code = `http_${String(response.status)}`;
    const retryAfterSeconds = parseRetryAfterSeconds(response.headers.get('Retry-After'));
    return new ApiError(
      response.status,
      code,
      messageForCode(code, response.status, retryAfterSeconds),
      {
        retryAfterSeconds,
      },
    );
  }

  const code = parsed.data.error;
  const retryAfterSeconds = parseRetryAfterSeconds(response.headers.get('Retry-After'));
  return new ApiError(
    response.status,
    code,
    messageForCode(code, response.status, retryAfterSeconds),
    {
      retryAfterSeconds,
      denialReason: parsed.data.denialReason,
    },
  );
}

/**
 * Perform a request and parse the response with `schema`.
 *
 * `credentials: 'same-origin'` because the session is a cookie and the API is same-origin in every
 * environment — through Vite's `/api` proxy in development and behind the same CloudFront
 * distribution in production. Nothing here needs, or should acquire, a cross-origin path.
 *
 * `X-Requested-With` is set on every request, unconditionally, because this is the one place that
 * does the sending — see `docs/decisions/csrf-posture.md`. The server only enforces it on
 * state-changing methods (`server/src/auth/csrf.ts`), but sending it here too costs nothing and means
 * no caller of `apiRequest` has to know the distinction exists.
 */
export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...options.headers,
  };
  const init: RequestInit = { method, headers, credentials: 'same-origin' };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    init.signal = signal;
  }

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (cause) {
    throw new ApiError(0, 'network_error', 'Could not reach the server.', { cause });
  }

  if (!response.ok) {
    throw await errorForResponse(response, method, path);
  }

  const raw = parseBody(await response.text());
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      'unexpected_response',
      `The server's response to ${method} ${path} did not match the shape this app expects.`,
      { cause: parsed.error },
    );
  }
  return parsed.data;
}

/**
 * Perform a `POST` and return the raw response body as a `Blob`, for an endpoint that returns a
 * generated file (a chart-export PDF, `chartExport.ts`) rather than JSON. Shares `apiRequest`'s
 * request setup and failure handling exactly (`errorForResponse` above) — the server's failure
 * envelope is always JSON regardless of what a success response returns. Only the success path
 * differs: there is no JSON schema to validate a binary body against, so a caller cannot get
 * `unexpected_response` from this function the way `apiRequest` can.
 */
export async function requestBlob(
  path: string,
  options: { readonly body?: unknown; readonly signal?: AbortSignal } = {},
): Promise<Blob> {
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  };
  const init: RequestInit = {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: JSON.stringify(options.body ?? {}),
  };
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (cause) {
    throw new ApiError(0, 'network_error', 'Could not reach the server.', { cause });
  }

  if (!response.ok) {
    throw await errorForResponse(response, 'POST', path);
  }

  return response.blob();
}

/**
 * Perform a `POST` (or `PUT`) with a `FormData` body — a file upload — and parse the JSON response
 * with `schema`. First caller: `api/profilePhoto.ts` (`BACKLOG.md` A12). Shares `apiRequest`'s
 * failure handling exactly (`errorForResponse`); differs only in that `Content-Type` is never set
 * explicitly here, matching `fetch`'s own documented behaviour for a `FormData` body — the browser
 * computes the real `multipart/form-data; boundary=...` value itself, and setting it by hand would
 * omit that boundary and produce a body the server's `c.req.parseBody()` cannot parse at all.
 */
export async function apiUpload<T>(
  path: string,
  schema: z.ZodType<T>,
  formData: FormData,
  options: { readonly method?: 'POST' | 'PUT'; readonly signal?: AbortSignal } = {},
): Promise<T> {
  const method = options.method ?? 'POST';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };
  const init: RequestInit = { method, headers, credentials: 'same-origin', body: formData };
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (cause) {
    throw new ApiError(0, 'network_error', 'Could not reach the server.', { cause });
  }

  if (!response.ok) {
    throw await errorForResponse(response, method, path);
  }

  const raw = parseBody(await response.text());
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      'unexpected_response',
      `The server's response to ${method} ${path} did not match the shape this app expects.`,
      { cause: parsed.error },
    );
  }
  return parsed.data;
}

/**
 * `Retry-After` as this codebase's server always sends it: a plain integer count of seconds
 * (`server/src/security/rateLimiter.ts`'s `secondsUntilWindowEnd`), never the HTTP-date alternative
 * the header format also allows — so a strict integer parse is enough, and anything else (header
 * absent, or a value this build does not recognise) degrades to `undefined` rather than a guess.
 */
export function parseRetryAfterSeconds(headerValue: string | null): number | undefined {
  if (headerValue === null) return undefined;
  const seconds = Number.parseInt(headerValue, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

export function messageForCode(code: string, status: number, retryAfterSeconds?: number): string {
  switch (code) {
    case 'unauthenticated':
      return 'Your session has ended. Sign in again to continue.';
    case 'invalid_credentials':
      return 'That email and password combination was not recognised.';
    case 'forbidden':
      return 'Your role does not permit that.';
    case 'csrf_header_missing':
      // Should not be reachable through this client, which always sets the header — a real
      // occurrence means something between the browser and the server stripped it (a misconfigured
      // proxy is the likely cause), not a user action.
      return 'The request could not be verified. Reload the page and try again.';
    case 'internal_error':
      return 'The server hit an unexpected error. Nothing was changed.';
    case 'rate_limited':
      return retryAfterSeconds === undefined
        ? 'Too many attempts. Wait a bit before trying again.'
        : `Too many attempts. Try again in ${String(retryAfterSeconds)} second${retryAfterSeconds === 1 ? '' : 's'}.`;
    // The clinical-chart surface's own failure vocabulary (`@emr/shared`'s `CLINICAL_ERROR_CODES`
    // and `PATIENT_ERROR_CODES`). `chart_access_denied` has its own, more specific rendering in
    // `routes/patients/chartAccess.ts` wherever the caller has an `ApiError.denialReason` to show;
    // this is the fallback for anywhere that only has the generic message.
    case 'chart_access_denied':
      return 'Access to this chart was refused.';
    case 'not_found':
      return 'That record does not exist, or is not in this practice.';
    case 'invalid_request':
      return 'That request was not in a shape the server accepts.';
    case 'mrn_taken':
      return 'That MRN is already assigned to another patient.';
    case 'already_resolved':
      return 'Someone else already resolved this duplicate-match candidate.';
    case 'note_not_editable':
      return 'This note can no longer be edited — it may already be signed.';
    case 'note_not_signable':
      return 'This note cannot be signed — it may not be a draft, or you may not be its author.';
    case 'note_not_cosignable':
      return 'This note cannot be co-signed yet — it has not been signed.';
    case 'cannot_cosign_own_signature':
      return 'You already signed this note. Co-signing needs a second, distinct reviewer.';
    case 'no_active_credential':
      return 'Co-signing needs an active, unexpired MD or DO credential on file. Add one from your profile.';
    case 'already_cosigned':
      return 'Someone else already co-signed this note.';
    case 'invalid_transition':
      return 'That status change is not a legal move from the current status.';
    // `BACKLOG.md` C18's patient-photo upload (`server/src/documents/patientPhotoRoutes.ts`,
    // `documents/storage.ts`'s `DocumentContentMismatchError`/`DocumentMalwareDetectedError`) — the
    // first client-facing route that can produce either code.
    case 'document_content_mismatch':
      return 'That file does not look like the image type it claims to be. Try a different file.';
    case 'document_malware_detected':
      return 'That file was flagged and could not be uploaded.';
    // `BACKLOG.md` C20's per-org admin gate (`server/src/help/chatRoutes.ts`) — a deliberate,
    // admin-controlled state, not an outage, so this reads as "ask your admin," not "try again."
    case 'ai_feature_disabled':
      return 'This AI feature is turned off for your practice. A practice administrator can turn it on in settings.';
    // `BACKLOG.md` A12's profile-photo upload (`api/profilePhoto.ts`) — the three ways
    // `server/src/auth/profilePhotoRoutes.ts` refuses a file, per `@emr/shared`'s
    // `PROFILE_PHOTO_ERROR_CODES`.
    case 'file_too_large':
      return 'That photo is too large. Choose a file under 5 MB.';
    case 'unsupported_file_type':
      return "That file doesn't look like a real JPEG or PNG image.";
    case 'malware_detected':
      return 'That file was flagged and could not be uploaded.';
    default:
      return `The request failed (HTTP ${String(status)}).`;
  }
}
